import ImageType from '@/enums/ImageType';
import VehicleType from '@/enums/VehicleType';
import ExtraData from '@/models/ExtraData';
import SearchQuery from '@/models/SearchQuery';
import IImageSearchModel from '@/models/image/interfaces/image-search-model.d';
import IDerivative from '@/models/vehicle/interfaces/derivative.d';
import IRange from '@/models/vehicle/interfaces/range.d';
import { store } from '@/store';
import { namespace } from 'vuex-class';
import { vehiclePaintCodeProviderService } from '@/services/VehiclePaintCodeProviderService';

const VehicleModule = namespace('vehicleModule');

class ImageUrlService {
  private get currentRange(): IRange {
    return store.getters['vehicleModule/currentRange'];
  }

  private get currentDerivative(): IDerivative {
    return store.getters['vehicleModule/currentDerivative'];
  }

  private get searchQuery(): SearchQuery {
    return store.getters['searchQueryModule/searchQuery'];
  }

  private get vehicleType(): string {
    return this.searchQuery.vehicleType === VehicleType.Car ? 'car' : 'van';
  }

  public get useImaginStudio(): boolean {
    return store.getters['initialSettingsModule/useImaginStudio'];
  }

  public get ImaginStudioZoomLevel(): number {
    return store.getters['initialSettingsModule/imaginStudioZoomLevel'];
  }

  public get useCapImagesRangeIds(): number[] {
    return store.getters['initialSettingsModule/useCapImagesRangeIds'];
  }

  private imageFallbackUrl: string = '';

  private imageType(vehicleImageType: ImageType): string {
    return vehicleImageType === ImageType.Lifestyle ? 'lifestyle' : 'basic';
  }

  public bodyStyleImageUrl(
    vehicleImageType: ImageType,
    imageSize: number
  ): string {
    const imageType = this.imageType(vehicleImageType);
    const images: IImageSearchModel[] = this.currentDerivative.images[imageType];
    return images.length > 0 ? `//image.uk-tla.com/api/v1/image/vehicle/${this.vehicleType}/${this.currentDerivative.manufacturerNameSlug}/${this.currentDerivative.rangeNameSlug}/${imageType}/${images[0].imageId}/${imageSize}` : '';
  }

  public rangeImageUrl(
    vehicleImageType: ImageType,
    imageSize: number
  ): string {
    const imageType = this.imageType(vehicleImageType);
    const images: IImageSearchModel[] = this.currentRange.images[imageType];
    return images.length > 0 ? `//image.uk-tla.com/api/v1/image/vehicle/${this.vehicleType}/${this.currentRange.manufacturerNameSlug}/${this.currentRange.rangeNameSlug}/${imageType}/${images[0].imageId}/${imageSize}` : '';
  }

  private getMake(): string {
    return this.currentRange?.manufacturerName;
  }

  private getModel(): string {
    return this.currentRange?.rangeName;
  }

  private getBodyStyle(): string {
    if (this.currentDerivative?.bodystyles?.length > 0) {
      return this.currentDerivative?.bodystyles[0];
    }
    const searchQuery = store.getters['searchQueryModule/searchQuery'];
    return searchQuery?.bodystyles?.length
      ? searchQuery.bodystyles[0]
      : '';
  }

  private getTrim(): string {
    if (this.currentDerivative?.trimName?.length > 0) {
      return this.currentDerivative?.trimName;
    }
    const searchQuery = store.getters['searchQueryModule/searchQuery'];
    return searchQuery?.trimName
      ? searchQuery.trimName
      : '';
  }

  private getColour(): string {
    return store.getters['enquiryModule/extraData']
      .find((x: ExtraData) => x.keyName === 'basicColour')?.keyValue || '';
  }

  public imageUrl(
    vehicleImageType: ImageType = ImageType.Basic,
    imageSize: number = 200,
    make: string = this.getMake(),
    model: string = this.getModel(),
    bodyStyle: string = this.getBodyStyle(),
    trimName: string = this.getTrim(),
    paintId: string | null = null,
    zoomLevel: number = this.ImaginStudioZoomLevel
  ): string {
    if (this.useImaginStudio && (!this.useCapImagesRangeIds || !this.useCapImagesRangeIds.includes(this.currentRange.rangeId))) {
      let url = `https://cdn-08.imagin.studio/getImage?&customer=gbaskaprice&make=${make}&modelFamily=${model}&modelVariant=${bodyStyle}&modelRange=${trimName}&zoomType=fullscreen&zoomLevel=${zoomLevel}`;
      paintId = paintId || vehiclePaintCodeProviderService.GetPaintCode(this.getColour(), make);
      if (paintId) {
        url += `&paintId=${paintId}`;
      }
      if (this.currentDerivative?.fuelTypeName) {
        url += `&powertrain=${this.currentDerivative?.fuelTypeName}`;
      }
      return url;
    }
    if (this.currentDerivative && this.bodyStyleImageUrl.length > 0) {
      return this.bodyStyleImageUrl(vehicleImageType, imageSize);
    }

    if (this.currentRange && this.rangeImageUrl.length > 0) {
      return this.rangeImageUrl(vehicleImageType, imageSize);
    }

    return this.imageFallbackUrl;
  }
}

// export a singleton instance in the global namespace
// eslint-disable-next-line import/prefer-default-export
export const imageUrlService = new ImageUrlService();
